import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useMediaQuery } from '@mui/material';
import { useBrightTheme, useDataLoad, useUserFunctionality, useUserSourceCode, useWidgetVisibility } from 'hooks';
import NoDataPlug from 'portfolio3/components/common/NoDataPlug';
import { WidgetContainer as Widget } from 'portfolio3/components/common/WidgetContainer';
import {
  WidgetContainerAddButton,
  WidgetContainerOptions,
  WidgetContainerTitle,
} from 'portfolio3/components/widgetContainerRecipes';
import { emitYMEvent } from 'portfolio3/features/yandexMetrika';
import { commonTheme } from 'portfolio3/styles/theme';

import { editStudentInterestActions } from '../../../../actions';
import { IChildInfo, ICommonResponse, ISaveInterestRequest } from '../../../../api/types';
import { InterestCarousel, InterestEditForm } from '../../../../components/redesignStudent';
import {
  profileNavigation,
  SectionCodeToKey,
  SectionNames,
  StudentSectionSettingTypeCodes,
  WIDGET_FILTER_MULTI_VALUE_ALL,
} from '../../../../const';
import { InterestsEditFormContext } from '../../../../context';
import { placeholderCharacter } from '../../../../images';
import { IRootState } from '../../../../reducers';
import { profileInterestsSelector } from '../../../../selectors';
import {
  IInterestEditFormAttributes,
  IInterestEditFormData,
  IInterestEntity,
  IProfileFilter,
  IProfileFilterItem,
} from '../../../../types';
import { Drawer } from '../../../../ui-kit';
import { getDataLoadRequests, isDefined } from '../../../../utils';
import ProfileFilter from './filters/profileFilter';
import ProfileNoDataButton from './noDataButton';

interface IInterestsContainerProps {
  currentStudent: IChildInfo;
  studentInterests: ICommonResponse<IInterestEntity>;
  linkMode: boolean | undefined;
  editStudentInterest: typeof editStudentInterestActions.request;
}

export const useInterestSectionLoader = () => {
  const sectionTypeCode = StudentSectionSettingTypeCodes.profile;
  const subsectionTypeCode = StudentSectionSettingTypeCodes.profileInterest;

  const { isSettingsMode, isWidgetVisible, toggleElement, overlayElement } = useWidgetVisibility({
    sectionTypeCode,
    subsectionTypeCode,
  });

  const dataRequests = getDataLoadRequests();
  useDataLoad({
    shouldLoad: isWidgetVisible,
    requests: [...dataRequests.profileInterests, ...dataRequests.archivedInterestRefs],
  });

  return { isSettingsMode, isWidgetVisible, toggleElement, overlayElement };
};

const InterestsContainer: React.FC<IInterestsContainerProps> = ({
  currentStudent,
  studentInterests,
  linkMode,
  editStudentInterest,
}) => {
  const interests: IInterestEntity[] = studentInterests.content;

  const isBrightTheme = useBrightTheme();
  const { isSettingsMode, isWidgetVisible, toggleElement, overlayElement } = useInterestSectionLoader();

  const { isStudent } = useUserFunctionality();
  const sourceCode = useUserSourceCode();

  const [filterValue, setFilterValue] = useState(WIDGET_FILTER_MULTI_VALUE_ALL);

  const [isEditFormDrawerOpen, setEditFormDrawerOpen] = useState(false);
  const [editFormAttributes, setEditFormAttributes] = useState<IInterestEditFormAttributes>();
  const [editFormData, setEditFormData] = useState<IInterestEditFormData>();

  const history = useHistory();
  const isShortTitle = useMediaQuery(commonTheme.breakpoints.down('commonMd'));
  const isMobile = useMediaQuery(commonTheme.breakpoints.down('commonSm'));

  const totalEntities = interests?.length ?? 0;

  const isAddButtonVisible = !linkMode && isStudent && !isSettingsMode;

  const noDataText = isStudent
    ? 'Давай скорее это исправим! Заполни анкету, которую мы подготовили, чтобы узнать немного больше о тебе'
    : 'Как только учащийся укажет свои интересы, информация о них отобразится здесь';

  const noDataButtonText = isShortTitle ? 'Поделиться интересами' : 'Поделиться интересами, увлечениями и хобби';

  const drawersAnchor = isMobile ? 'bottom' : 'right';

  const sectionFilters = useMemo(() => {
    const sectionFiltersCount = interests.reduce<IProfileFilter>((current, interest) => {
      const interestSectionCodes = interest.actionKinds.map((actionKind) => actionKind.sectionCode).filter(isDefined);

      const sectionKeyToFilter = new Map<string, IProfileFilterItem>();
      interestSectionCodes.forEach((sectionCode) => {
        const key = SectionCodeToKey[sectionCode];
        const currentCount = current[key]?.count || 0;

        sectionKeyToFilter.set(key, {
          code: sectionCode,
          value: SectionNames[key],
          count: currentCount + 1,
        });
      });

      return {
        ...current,
        ...Object.fromEntries(sectionKeyToFilter),
      };
    }, {});

    const filters = Object.values(sectionFiltersCount);
    filters.unshift({
      code: WIDGET_FILTER_MULTI_VALUE_ALL,
      value: 'Все',
      count: totalEntities,
    });

    return filters.sort((a, b) => a.code - b.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interests]);

  const filteredEntities = useMemo(() => {
    if (filterValue === WIDGET_FILTER_MULTI_VALUE_ALL) return interests;

    return interests.filter((interest) => {
      return interest.actionKinds.some((actionKind) => {
        return actionKind.sectionCode ? actionKind.sectionCode === filterValue : false;
      });
    });
  }, [interests, filterValue]);

  useEffect(() => {
    if (filteredEntities.length === 0) setFilterValue(WIDGET_FILTER_MULTI_VALUE_ALL);
  }, [filteredEntities]);

  const handleFilterInterests = (value: number) => {
    setFilterValue(value);
  };

  const handleAddButtonClick = () => {
    const pageBase = history.location.pathname.split('/')[1];
    history.push(`/${pageBase}/interests`);

    emitYMEvent({ type: 'addInterestClick' });
  };

  const handleCloseEditFormDrawer = () => {
    setEditFormDrawerOpen(false);
  };

  const handleSaveEditFormChanges = (interestId: number, formData: IInterestEditFormData) => {
    setEditFormDrawerOpen(false);

    if (!currentStudent.meshId || !sourceCode) return;

    const changedInterest = interests.find((interest) => interest.id === interestId);
    if (!changedInterest) return;

    const saveInterestRequest: ISaveInterestRequest = {
      personId: currentStudent.meshId,
      sourceCode,
      interestCode: changedInterest.code,
      interestHeadCode: changedInterest.headCode,
      interestActionCode: formData.actions.filter((action) => action.isActive).map((action) => action.code),
      subinterestCode:
        formData.categories && formData.categories.length > 0
          ? formData.categories.filter((category) => category.isActive).map((category) => category.code)
          : null,
    };

    editStudentInterest(saveInterestRequest, changedInterest.id, currentStudent.meshId);
  };

  const hasData = filteredEntities && filteredEntities.length > 0;

  if (!isWidgetVisible) return null;

  return (
    <InterestsEditFormContext.Provider
      value={{
        onOpen: () => setEditFormDrawerOpen(true),
        setEditFormAttributes,
        setEditFormData,
      }}
    >
      <Widget containerData={profileNavigation.interest} withBackground={isBrightTheme}>
        {overlayElement}
        <Widget.Header
          title={<WidgetContainerTitle title="Анкета интересов" right={isSettingsMode && toggleElement} />}
          options={
            hasData &&
            sectionFilters.length > 1 && (
              <WidgetContainerOptions
                left={
                  interests.length > 0 && (
                    <ProfileFilter
                      filterValue={filterValue}
                      filters={sectionFilters}
                      onFilterChange={handleFilterInterests}
                    />
                  )
                }
                right={
                  isAddButtonVisible &&
                  hasData && (
                    <WidgetContainerAddButton isMobile={isMobile} isReward={false} onClick={handleAddButtonClick} />
                  )
                }
              />
            )
          }
        />
        <Widget.Padding disableRightPadding={isMobile && hasData}>
          {hasData ? (
            <InterestCarousel interests={filteredEntities} />
          ) : (
            <NoDataPlug
              image={placeholderCharacter}
              title="Интересов, увлечений и хобби пока нет"
              subtitle={noDataText}
              loading={studentInterests.loading}
            >
              {isAddButtonVisible && <ProfileNoDataButton label={noDataButtonText} onClick={handleAddButtonClick} />}
            </NoDataPlug>
          )}
        </Widget.Padding>
      </Widget>
      {editFormAttributes && editFormData && !isSettingsMode && (
        /* TODO перенести на CommonDataDrawer */
        <Drawer
          isContainerScrollable={false}
          isFullHeight
          anchor={drawersAnchor}
          transitionDuration={{ appear: 300, enter: 300, exit: 250 }}
          open={isEditFormDrawerOpen}
          onClose={handleCloseEditFormDrawer}
        >
          <InterestEditForm
            formAttributes={editFormAttributes}
            formData={editFormData}
            onClose={handleCloseEditFormDrawer}
            onSave={handleSaveEditFormChanges}
          />
        </Drawer>
      )}
    </InterestsEditFormContext.Provider>
  );
};

export default connect(
  (state: IRootState) => ({
    currentStudent: state.currentStudent,
    studentInterests: profileInterestsSelector(state),
    linkMode: state.linkMode.mode,
  }),
  {
    editStudentInterest: editStudentInterestActions.request,
  },
)(InterestsContainer);
